import React, { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { TextField, IconButton } from "@mui/material/";
import { strings } from "../../../../common/i18n";
import { format } from "../../../../common/lib";
import actions from "../../../../Store/actions";
import EditIcon from "@mui/icons-material/Edit";
import UserLocationsSelect from "./UserLocationsSelect";
import reconcileAuditors from "./reconcileAuditors";
import { logToSentry } from "../../../../config/Sentry";
/**
 *  Hook for user table data and operations
 */

export default () => {
    const [loading, setLoading] = useState(false);
    const [isModalShown, setIsModalShown] = useState(false);
    const [amendedAudit, setAmendedAudit] = useState({ rowId: 0, name: "" });

    const { auditsCreate, accounts, typesList } = useSelector((state) => state);

    const dispatch = useDispatch();

    useEffect(() => {
        let isMounted = true;
        (async () => {
            const date = format.dateTimeNew(moment().toISOString());
            try {
                const next = [...auditsCreate.data];
                let changed = false;
                next.forEach((item) => {
                    if (!item.nameEdited && !item.name.includes("\\")) {
                        item.name = `${item.name} \\ ${date}`;
                        changed = true;
                    }
                });
                if (changed)
                    dispatch(actions.auditsCreate.create.update({ data: next }));

                if (isMounted) setLoading(true);
                if (!accounts.data.length)
                    await dispatch(actions.accounts.create.getRequest());
            } catch (error) {
                console.error(error);
                logToSentry("Error creating an audit", { error });
            }
            if (isMounted) setLoading(false);
        })();
        return () => { isMounted = false; };
    }, []);

    /** Check for audit select option in typeslist */
    const auditorSelect = useMemo(() => {
        /**id for auto select all auditor */
        const autoSelectAuditorValId = 1;
        const { organisationSettings } = typesList;
        const { data } = organisationSettings;
        if (data.length > 0)
            return data
                .find((item) => item.settingId === autoSelectAuditorValId)
                .value.toLowerCase();
        else return "true"; // Adding default true when no options present
    }, [typesList]);

    /** Handle value change of the auditors */
    const onValueChange = useCallback(
        (name: string) => (value: any, index: number, selectedIds) => {
            //If no auditors present get from the rows
            const auditorList =
                auditsCreate.auditors.length > 0 ? auditsCreate.auditors : rows;
            setLoading(true);
            const next = [...auditsCreate.data];
            const newAuditors = [...auditorList];
            next[index][name] = value;
            const res = newAuditors.find((item) => item.id === index);
            res["selectedUsers"] = selectedIds;
            res["selectedAuditors"] = value.length > 0 ? "true" : "false";

            dispatch(
                actions.auditsCreate.create.update({
                    data: next,
                    auditors: newAuditors
                })
            );
            setLoading(false);
        },
        [auditsCreate]
    );

    /**  Audit name  change  */
    const onAuditNameChange = (name, rowId) => {
        setAmendedAudit({ rowId, name });
        setIsModalShown(true);
    };

    // TODO: it should filter invalid characters
    const onNameChange = (e) => {
        const { value } = e.target;

        setAmendedAudit({ rowId: amendedAudit.rowId, name: value });
    };

    /** On saving audit name */
    const onAuditNameSave = useCallback(() => {
        const { rowId, name } = amendedAudit;
        const next = [...auditsCreate.data];
        next[rowId].name = name;
        next[rowId].nameEdited = true;
        dispatch(actions.auditsCreate.create.update({ data: next }));
        setIsModalShown(false);
    }, [amendedAudit, auditsCreate]);

    const onClose = () => setIsModalShown(false);

    /** On creating a columns */
    const columns = [
        {
            accessorKey: "auditName",
            header: strings("auditName"),
            enableSorting: false,
            Cell: ({ row }) => (
                <>
                    <TextField
                        variant="standard"
                        id="audit-name-id"
                        value={row.original.auditName}
                        fullWidth
                        disabled
                    />
                    <IconButton
                        color="primary"
                        aria-label="upload picture"
                        component="span"
                        onClick={() =>
                            onAuditNameChange(
                                row.original.auditName,
                                row.original.id
                            )
                        }
                        size="large"
                    >
                        <EditIcon />
                    </IconButton>
                </>
            )
        },
        {
            accessorKey: "branchName",
            header: strings("branch")
        },
        {
            accessorKey: "locationName",
            header: strings("location")
        },
        {
            accessorKey: "userLocations",
            header: strings("auditors"),
            enableSorting: false,
            Cell: ({ row }) => (
                <UserLocationsSelect
                    defaultValue={row.original.userLocations}
                    index={row.original.id}
                    branch={row.original.branch}
                    branchLocation={row.original.location}
                    onChange={onValueChange("userLocations")}
                />
            )
        }
    ];

    /** On creating a rows  */
    const rows = useMemo(() => {
        return auditsCreate.data.reduce((accum, item, index) => {
            if (
                item.selected &&
                auditsCreate.selectedLocations.ids.includes(item.location)
            )
                accum.push({
                    id: index,
                    name: item.name,
                    branch: item.branch,
                    branchName: item.branchName,
                    branchRef: item.branchRef,
                    location: item.location,
                    locationName: item.locationName,
                    userLocations: item.userLocations,
                    auditName: item.name,
                    selectedAuditors: auditorSelect,
                    selectedUsers: []
                });
            return accum;
        }, []);
    }, [auditsCreate.data, auditsCreate.selectedLocations.ids, auditorSelect]);

    /** Keep the stored auditor picks in step with the selected audits — see
     * reconcileAuditors.js. Writing them only while empty left them stale
     * after Back + a changed branch/location selection. */
    useEffect(() => {
        const next = reconcileAuditors(auditsCreate.auditors, rows);
        if (next) dispatch(actions.auditsCreate.create.update({ auditors: next }));
    }, [auditsCreate.auditors, rows]);

    return {
        loading,
        rows,
        columns,
        isModalShown,
        amendedAudit,
        setAmendedAudit,
        onAuditNameSave,
        onNameChange,
        onClose
    };
};

//TODO 1. Revisit this issue later for the name and timestamp issue for the audit name
//2. The time when we go back and come forward the time is still the same when the time is changing
//3. On selecting multiple branches the selectedUser will break if the users are not assigned

//TODO: To prevent date and time duplication